import React, { useState, useEffect } from 'react'
import styles from '../../styles/mainContent/Projects.module.css';
import overlayStyles from '../../styles/overlays/ProjectOverlay.module.css';
import utilities from '../../styles/utilities.module.css';
import Masonry from 'react-masonry-css';
import { projectData, Project } from '../../data/projects';
import Overlay from '../overlays/Overlay';
import GitHubReadme from '../overlays/GitHubReadme';
import { FaGithub } from 'react-icons/fa';
import { IoClose } from 'react-icons/io5';
import githubInteraction from '../../utils/GithubInteraction';
import { useGlobalLayerState } from '../../hooks/useLayerState';
import { OverlayLayer } from '../LayerWrapper';
import { wrapIcon } from '../../utils/iconWrapper';

const GitHubIcon = wrapIcon(FaGithub);
const CloseIcon = wrapIcon(IoClose);

interface ProjectsProps {
  isSmallScreen: boolean;
}

const Projects: React.FC<ProjectsProps> = ({ isSmallScreen }) => {
  const breakpointColumns = {
    default: 3,
    1100: 2,
    700: 1
  };

  // Store fetched topics for each project
  const [projectTopics, setProjectTopics] = useState<{[key: string]: string[]}>({});
  
  // Store fetched descriptions for each project
  const [projectDescriptions, setProjectDescriptions] = useState<{[key: string]: string}>({});
  
  // Loading state for GitHub data
  const [isLoadingGitHubData, setIsLoadingGitHubData] = useState(true);
  
  // Layer state management for overlay
  const { showOverlay, showDefault, getCurrentPreset } = useGlobalLayerState();
  const isOverlayActive = getCurrentPreset() === 'OVERLAY_ACTIVE';
  
  // Local state for selected project and readme expansion
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [isReadmeExpanded, setIsReadmeExpanded] = useState(false);

  // Fetch topics and descriptions for all projects when component mounts
  useEffect(() => {
    const fetchAllData = async () => {
      setIsLoadingGitHubData(true);
      
      try {
        const projectUrls = projectData.map(project => project.link);
        const { topics, descriptions } = await githubInteraction.batchGetRepositoryData(projectUrls);
        
        // Convert URL-keyed data to project ID-keyed data
        const topicsData: {[key: string]: string[]} = {};
        const descriptionsData: {[key: string]: string} = {};
        
        projectData.forEach(project => {
          topicsData[project.id] = topics[project.link] || [];
          descriptionsData[project.id] = descriptions[project.link] || '';
        });
        
        setProjectTopics(topicsData);
        setProjectDescriptions(descriptionsData);
      } catch (error) {
        console.error('Failed to fetch GitHub data:', error);
      } finally {
        setIsLoadingGitHubData(false);
      }
    };
    
    fetchAllData();
  }, []);

  // Function to render topic data
  const renderTopics = (projectId: number) => {
    const topics = projectTopics[projectId];
    
    // Show loading state while fetching
    if (isLoadingGitHubData) {
      return (
        <div className={styles.languageContainer}>
          <div className={`${styles.languageList} ${utilities.flexWrap}`}>
            <div className={styles.language}>
              Loading topics...
            </div>
          </div>
        </div>
      );
    }
    
    // Don't render anything if no topics data or if empty
    if (!topics || topics.length === 0) {
      return null;
    }
    
    return (
      <div className={styles.languageContainer}>
        <div className={styles.languageList}>
          {topics.map((topic) => (
            <div key={topic} className={styles.language}>
              {topic}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Handle project card click
  const handleProjectClick = (project: Project) => {
    setSelectedProject(project);
    showOverlay();
  };

  // Handle overlay close
  const handleCloseOverlay = () => {
    showDefault();
    setSelectedProject(null);
    setIsReadmeExpanded(false);
  };

  return (
    <section id="projects" className={utilities.container}>
      {/* Section title. */}
      <div className={styles.subsection}>
        <h2>Projects</h2>
      </div>
      {/* Section Contents */}
      <Masonry
        breakpointCols={breakpointColumns}
        className={styles.masonryGrid}
        columnClassName={styles.masonryColumn}
      >
        {projectData.map((project) => {
          const githubDescription = projectDescriptions[project.id];
          const displayDescription = isLoadingGitHubData 
            ? "Loading description..." 
            : (githubDescription || project.description || "No description available.");
          
          return (
            <div 
              key={project.id} 
              className={`${styles.projectCard} ${styles.masonryItem} ${styles.clickable}`}
              onClick={() => handleProjectClick(project)}
            >
              <h3>{project.title}</h3>
              <p>{displayDescription}</p>
              {renderTopics(project.id)}
              <div className={styles.clickHint}>Click for details</div>
            </div>
          );
        })}
      </Masonry>
      
      <OverlayLayer>
        <Overlay 
          isVisible={isOverlayActive}
          isSmallScreen={isSmallScreen}
          onClose={handleCloseOverlay}
          isExpanded={isReadmeExpanded}
        >
        {selectedProject && (
          <div className={`${overlayStyles.projectOverlay} ${isReadmeExpanded ? overlayStyles.expanded : ''}`}>
            {/* Header Section */}
            <div className={overlayStyles.projectHeader}>
              <div className={`${overlayStyles.titleRow} ${utilities.flexSpaceBetween}`}>
                <h2>{selectedProject.title}</h2>
                <div className={`${overlayStyles.buttonGroup} ${utilities.flexCenter}`}>
                  <a 
                    href={selectedProject.link} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className={`${overlayStyles.viewProjectButton} ${utilities.flexCenter}`}
                    title="View on GitHub"
                  >
                    <GitHubIcon />
                  </a>
                  <button 
                    onClick={handleCloseOverlay}
                    className={`${overlayStyles.closeButton} ${utilities.flexCenter}`}
                    title="Close"
                  >
                    <CloseIcon />
                  </button>
                </div>
              </div>
              <p className={overlayStyles.projectDescription}>
                {isLoadingGitHubData 
                  ? "Loading description..." 
                  : (projectDescriptions[selectedProject.id] || selectedProject.description || "No description available for this project yet.")}
              </p>
              
              {projectTopics[selectedProject.id] && projectTopics[selectedProject.id].length > 0 && (
                <div className={overlayStyles.overlayLanguages}>
                  <div className={overlayStyles.languageContainer}>
                    <div className={`${overlayStyles.languageList} ${utilities.flexWrap}`}>
                      {projectTopics[selectedProject.id].map((topic) => (
                        <div key={topic} className={overlayStyles.language}>
                          {topic}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* README Section */}
            <div className={`${overlayStyles.readmeSection} ${isReadmeExpanded ? overlayStyles.expanded : ''}`}>
              <GitHubReadme 
                githubUrl={selectedProject.link}
                isSmallScreen={isSmallScreen}
                onExpandToggle={(isExpanded) => {
                  setIsReadmeExpanded(isExpanded);
                }}
              />
            </div>
          </div>
        )}
        </Overlay>
      </OverlayLayer>
    </section>
  );
};

export default Projects;